import { useId } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { Box, useTheme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { CodeSnippet, Progress } from '@backstage/core-components';

// mermaid pesa ~1 MB: se importa bajo demanda para que solo lo descargue quien
// abre un golden path con diagramas. `securityLevel: 'strict'` hace que mermaid
// sanee las etiquetas (DOMPurify interno) y desactive clicks/JS del diagrama, que
// es lo que permite insertar su SVG con dangerouslySetInnerHTML.
//
// `suppressErrorRendering: true` (fix round 1): en mermaid 12, cuando `render()`
// falla, mermaid inserta su propio `<div>` temporal de error en el DOM y lo retira
// antes de relanzar la excepción — pero sin esta opción, además, deja pintado su
// SVG de error colgando de `<body>` (fuera de este componente), aunque aquí
// pintemos el aviso + el código fuente. Con la opción activa no llega a insertar
// ese SVG.
export const MermaidDiagram = ({ code }: { code: string }) => {
  const theme = useTheme();
  const id = `remote-doc-mermaid-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const { loading, error, value } = useAsync(async () => {
    const mermaid = (await import('mermaid')).default;
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      suppressErrorRendering: true,
      theme: theme.palette.type === 'dark' ? 'dark' : 'default',
    });
    return (await mermaid.render(id, code)).svg;
  }, [code, id, theme.palette.type]);

  if (loading) return <Progress />;
  if (error || !value) {
    return (
      <>
        <Alert severity="warning">No se pudo dibujar el diagrama; se muestra su definición.</Alert>
        <CodeSnippet language="text" text={code} />
      </>
    );
  }
  return (
    <Box my={2} display="flex" justifyContent="center" dangerouslySetInnerHTML={{ __html: value }} />
  );
};
